import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useDashboardTheme } from "../../../../context/DashboardThemeContext";
import { useAlert } from "../../../../context/AlertContext";

const API_BASE = "/api/access-control";

function getToken() {
  return localStorage.getItem("accessToken") || localStorage.getItem("access") || "";
}

function authHeaders() {
  return {
    Authorization: `Bearer ${getToken()}`,
    "Content-Type": "application/json",
  };
}

export default function PermissionsSettings() {
  const { dashboardTheme } = useDashboardTheme();
  const { showSuccess, showError, showInfo } = useAlert();

  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const selectedUser = useMemo(() => {
    return users.find((item) => item.id === selectedUserId) || null;
  }, [users, selectedUserId]);

  const groupedPermissions = useMemo(() => {
    return permissions.reduce((acc, permission) => {
      const key = permission.module_label || permission.module;

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(permission);
      return acc;
    }, {});
  }, [permissions]);

  const loadData = async () => {
    setLoading(true);

    try {
      const [usersResponse, permissionsResponse] = await Promise.all([
        fetch(`${API_BASE}/users/`, {
          headers: authHeaders(),
        }),
        fetch(`${API_BASE}/permissions/`, {
          headers: authHeaders(),
        }),
      ]);

      const usersData = await usersResponse.json();
      const permissionsData = await permissionsResponse.json();

      if (!usersResponse.ok) {
        throw new Error(usersData.detail || usersData.error || "No se pudieron cargar los usuarios.");
      }

      if (!permissionsResponse.ok) {
        throw new Error(permissionsData.detail || permissionsData.error || "No se pudieron cargar los permisos.");
      }

      const loadedUsers = usersData.results || [];
      const loadedPermissions = permissionsData.results || [];

      setUsers(loadedUsers);
      setPermissions(loadedPermissions);

      if (loadedUsers.length > 0) {
        setSelectedUserId(loadedUsers[0].id);
        setSelectedPermissions(loadedUsers[0].permissions || []);
      } else {
        showInfo("No hay usuarios disponibles.");
      }
    } catch (err) {
      showError(err.message || "Error al cargar permisos.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUserId(user.id);
    setSelectedPermissions(user.permissions || []);
  };

  const handleTogglePermission = (code) => {
    setSelectedPermissions((prev) => {
      if (prev.includes(code)) {
        return prev.filter((item) => item !== code);
      }

      return [...prev, code];
    });
  };

  const handleSave = async () => {
    if (!selectedUser) {
      showInfo("Selecciona un usuario.");
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${API_BASE}/users/${selectedUser.id}/permissions/`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          permissions: selectedPermissions,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.error || "No se pudieron guardar los permisos.");
      }

      setUsers((prev) =>
        prev.map((user) =>
          user.id === selectedUser.id
            ? { ...user, permissions: data.permissions || selectedPermissions }
            : user
        )
      );

      showSuccess("Permisos actualizados correctamente.");
    } catch (err) {
      showError(err.message || "Error al guardar permisos.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "24px",
        border: `1px solid ${dashboardTheme.border_color}`,
        bgcolor: dashboardTheme.card_background,
        p: { xs: 2.5, md: 3 },
      }}
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography
            sx={{
              fontSize: { xs: 24, md: 30 },
              fontWeight: 600,
              color: dashboardTheme.text_color,
              lineHeight: 1.2,
            }}
          >
            Control de accesos
          </Typography>

          <Typography
            sx={{
              mt: 0.7,
              fontSize: 14,
              fontWeight: 400,
              color: dashboardTheme.muted_text_color,
              lineHeight: 1.6,
            }}
          >
            Asigna qué usuarios pueden ver, crear, editar o eliminar módulos del sistema.
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack direction={{ xs: "column", md: "row" }} spacing={2.5}>
            <Paper
              elevation={0}
              sx={{
                width: { xs: "100%", md: 330 },
                borderRadius: "18px",
                border: `1px solid ${dashboardTheme.border_color}`,
                bgcolor: dashboardTheme.dashboard_background,
                overflow: "hidden",
              }}
            >
              <Box sx={{ p: 2 }}>
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: dashboardTheme.text_color,
                  }}
                >
                  Usuarios
                </Typography>
              </Box>

              <Divider sx={{ borderColor: dashboardTheme.border_color }} />

              <Stack>
                {users.map((user) => {
                  const active = user.id === selectedUserId;

                  return (
                    <Box
                      key={user.id}
                      onClick={() => handleSelectUser(user)}
                      sx={{
                        p: 2,
                        cursor: "pointer",
                        borderBottom: `1px solid ${dashboardTheme.border_color}`,
                        bgcolor: active ? `${dashboardTheme.primary_color}18` : "transparent",
                        "&:hover": {
                          bgcolor: `${dashboardTheme.primary_color}12`,
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: dashboardTheme.text_color,
                        }}
                      >
                        {user.name}
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.3,
                          fontSize: 12,
                          color: dashboardTheme.muted_text_color,
                        }}
                      >
                        {user.email}
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.4,
                          fontSize: 12,
                          color: dashboardTheme.muted_text_color,
                        }}
                      >
                        {user.role}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Paper>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              {!selectedUser ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: "18px",
                    border: `1px solid ${dashboardTheme.border_color}`,
                    bgcolor: dashboardTheme.dashboard_background,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: dashboardTheme.muted_text_color,
                    }}
                  >
                    Selecciona un usuario.
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 600,
                        color: dashboardTheme.text_color,
                      }}
                    >
                      {selectedUser.name}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 13,
                        color: dashboardTheme.muted_text_color,
                      }}
                    >
                      {selectedUser.email}
                    </Typography>
                  </Box>

                  {Object.entries(groupedPermissions).map(([group, items]) => (
                    <Paper
                      key={group}
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: "18px",
                        border: `1px solid ${dashboardTheme.border_color}`,
                        bgcolor: dashboardTheme.dashboard_background,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: 600,
                          mb: 1.5,
                          color: dashboardTheme.text_color,
                        }}
                      >
                        {group}
                      </Typography>

                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {items.map((permission) => (
                          <FormControlLabel
                            key={permission.code}
                            control={
                              <Checkbox
                                checked={selectedPermissions.includes(permission.code)}
                                onChange={() => handleTogglePermission(permission.code)}
                                sx={{
                                  color: dashboardTheme.muted_text_color,
                                  "&.Mui-checked": {
                                    color: dashboardTheme.primary_color,
                                  },
                                }}
                              />
                            }
                            label={permission.name}
                            sx={{
                              minWidth: { xs: "100%", sm: 190 },
                              m: 0,
                              color: dashboardTheme.text_color,
                              "& .MuiFormControlLabel-label": {
                                fontSize: 14,
                              },
                            }}
                          />
                        ))}
                      </Stack>
                    </Paper>
                  ))}

                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      disabled={saving}
                      onClick={handleSave}
                      sx={{
                        minHeight: 42,
                        px: 2.5,
                        borderRadius: "14px",
                        textTransform: "none",
                        fontSize: 14,
                        fontWeight: 600,
                        bgcolor: dashboardTheme.button_color,
                        color: dashboardTheme.button_text_color,
                        boxShadow: "none",
                        "&:hover": {
                          bgcolor: dashboardTheme.button_color,
                          boxShadow: "none",
                        },
                      }}
                    >
                      {saving ? "Guardando..." : "Guardar permisos"}
                    </Button>
                  </Box>
                </Stack>
              )}
            </Box>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}